import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../layouts/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { CircleNotch, User, Lock, Buildings } from '@phosphor-icons/react';
import { profileApi } from '../lib/api';
import { toast } from 'sonner';
import {
  PASSWORD_POLICY_HINT,
  passwordErrorMessage,
  validatePasswordParts,
} from '../lib/passwordValidation';

export default function ProfilePage() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPw, setChangingPw] = useState(false);

  const loadProfile = useCallback(async () => {
    try {
      const res = await profileApi.get();
      setProfile(res.data);
      setName(res.data.name || '');
    } catch { toast.error('Failed to load profile'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const res = await profileApi.update({ name: name.trim() });
      setProfile(res.data);
      toast.success('Profile updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    const passwordCheck = validatePasswordParts(newPassword);
    if (!passwordCheck.valid) {
      toast.error(passwordErrorMessage(passwordCheck, t));
      return;
    }
    setChangingPw(true);
    try {
      await profileApi.changePassword({ current_password: currentPassword, new_password: passwordCheck.password });
      toast.success('Password changed successfully');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change password'); }
    finally { setChangingPw(false); }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-20"><CircleNotch className="w-8 h-8 animate-spin text-brand-500" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl space-y-6" data-testid="profile-page">
        <PageHeader
          title={t('profile.title')}
        />

        {/* Info Card */}
        <Card className={isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}>
          <CardHeader className="pb-3">
            <CardTitle className={`text-base flex items-center gap-2 ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
              <User className="w-4 h-4 text-brand-500" /> {t('profile.accountInfo')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-zinc-300' : ''}>{t('common.email')}</Label>
                <Input value={profile?.email || ''} disabled className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-400' : 'bg-zinc-50 text-zinc-500'} data-testid="profile-email" />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-zinc-300' : ''}>{t('common.name')}</Label>
                <Input value={name} onChange={e => setName(e.target.value)} className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="profile-name" />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-zinc-300' : ''}>{t('profile.role')}</Label>
                <Input value={profile?.role || ''} disabled className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-400' : 'bg-zinc-50 text-zinc-500'} />
              </div>
              <Button type="submit" disabled={saving} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="save-profile-btn">
                {saving ? <CircleNotch className="w-4 h-4 animate-spin mr-2" /> : null} {t('profile.saveChanges')}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Org Card */}
        {profile?.organization && (
          <Card className={isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}>
            <CardHeader className="pb-3">
              <CardTitle className={`text-base flex items-center gap-2 ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
                <Buildings className="w-4 h-4 text-brand-500" /> {t('profile.organization')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className={isDark ? 'text-zinc-500' : 'text-zinc-400'}>{t('common.name')}</span><p className={`mt-0.5 ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{profile.organization.name}</p></div>
                {profile.organization.address && <div><span className={isDark ? 'text-zinc-500' : 'text-zinc-400'}>{t('profile.address')}</span><p className={`mt-0.5 ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{profile.organization.address}</p></div>}
                {profile.organization.phone && <div><span className={isDark ? 'text-zinc-500' : 'text-zinc-400'}>{t('profile.phone')}</span><p className={`mt-0.5 ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{profile.organization.phone}</p></div>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Password Card */}
        <Card className={isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}>
          <CardHeader className="pb-3">
            <CardTitle className={`text-base flex items-center gap-2 ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
              <Lock className="w-4 h-4 text-brand-500" /> {t('profile.changePassword')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-zinc-300' : ''}>{t('profile.currentPassword')}</Label>
                <Input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="current-password" />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-zinc-300' : ''}>{t('profile.newPassword')}</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder={t('auth.passwordRequirementsHint', { defaultValue: PASSWORD_POLICY_HINT })}
                  className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''}
                  data-testid="new-password"
                />
                <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
                  {t('auth.passwordRequirementsHint', { defaultValue: PASSWORD_POLICY_HINT })}
                </p>
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-zinc-300' : ''}>{t('profile.confirmPassword')}</Label>
                <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="confirm-password" />
              </div>
              <Button type="submit" disabled={changingPw || !currentPassword || !newPassword || !confirmPassword} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="change-password-btn">
                {changingPw ? <CircleNotch className="w-4 h-4 animate-spin mr-2" /> : null} {t('profile.changePassword')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
